import { A } from '@solidjs/router';
import { createMemo, createSignal, onCleanup, onMount, Show } from 'solid-js';
import { locale, t } from '@/shared/i18n';
import { trackMarketingEvent } from '@/shared/lib/marketing-analytics';
import FeatureMarketingLayout from './components/FeatureMarketingLayout';
import FeatureFaqSection from './components/FeatureFaqSection';

export default function InventoryManagementPage() {
  onMount(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'SoftwareApplication',
          name: 'khoai.io — Inventory Management',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web, iOS, Android',
          url: 'https://khoai.io/features/inventory-management',
          description:
            'Real-time stock counts, traceable adjustments, and controlled stock transfers across multiple warehouses and stores.',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
          publisher: {
            '@type': 'Organization',
            name: 'khoai.io',
            url: 'https://khoai.io',
          },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://khoai.io',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Inventory Management',
              item: 'https://khoai.io/features/inventory-management',
            },
          ],
        },
      ],
    };
    let script = document.getElementById(
      'ld-json-feature-inventory-management'
    ) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'ld-json-feature-inventory-management';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);
    onCleanup(() =>
      document.getElementById('ld-json-feature-inventory-management')?.remove()
    );
  });

  const [stockImageFailed, setStockImageFailed] = createSignal(false);
  const [adjustmentImageFailed, setAdjustmentImageFailed] = createSignal(false);
  const [transferImageFailed, setTransferImageFailed] = createSignal(false);
  const [csvSpreadsheetFailed, setCsvSpreadsheetFailed] = createSignal(false);
  const [csvResultFailed, setCsvResultFailed] = createSignal(false);
  const localizedDemoImage = createMemo(() =>
    locale() === 'vi'
      ? '/per_location_stock_demo_vn.jpg'
      : '/per_location_stock_demo_en.jpg'
  );
  const localizedAdjustmentImage = createMemo(() =>
    locale() === 'vi'
      ? '/adjustment_integrity_vn.jpg'
      : '/adjustment_integrity_en.jpg'
  );
  const localizedTransferImage = createMemo(() =>
    locale() === 'vi'
      ? '/transfer_lifecycle_vn.jpg'
      : '/transfer_lifecycle_en.jpg'
  );
  const localizedCsvSpreadsheet = createMemo(() =>
    locale() === 'vi' ? '/csv_spreadsheet_vn.jpg' : '/csv_spreadsheet_en.jpg'
  );
  const localizedCsvResult = createMemo(() =>
    locale() === 'vi' ? '/csv_result_vn.jpg' : '/csv_result_en.jpg'
  );
  return (
    <FeatureMarketingLayout
      pageTag={t('feature.inventory.tag')}
      title={t('feature.inventory.title')}
      subtitle={t('feature.inventory.subtitle')}
      lightTheme
      heroRevealOnScroll
    >
      <section class="bg-amber-50/55 py-16">
        <div class="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[0.72fr_1.28fr] lg:items-center lg:px-8">
          <div class="p-2 sm:p-4 lg:pr-2">
            <h2 class="text-3xl font-black text-slate-900">
              {t('feature.inventory.f1')}
            </h2>
            <p class="mt-4 text-base leading-relaxed text-slate-700">
              {t('feature.inventory.f1Desc')}
            </p>
            <ul class="mt-6 list-inside list-disc space-y-3 text-sm text-slate-700">
              <li>{t('feature.inventory.f1Bullet1')}</li>
              <li>{t('feature.inventory.f1Bullet2')}</li>
              <li>{t('feature.inventory.f1Bullet3')}</li>
            </ul>
          </div>
          <div class="p-0 sm:p-2 lg:pl-2">
            <Show
              when={!stockImageFailed()}
              fallback={
                <div class="flex min-h-[340px] w-full items-center justify-center rounded-2xl bg-slate-50/70 p-6 text-center text-sm text-slate-500">
                  {t('feature.inventory.visual1Caption')}
                </div>
              }
            >
              <img
                src={localizedDemoImage()}
                alt={t('feature.inventory.visual1Title')}
                class="block h-auto w-full rounded-2xl object-contain shadow-[0_18px_48px_rgba(15,23,42,0.12)]"
                loading="lazy"
                onError={() => setStockImageFailed(true)}
              />
            </Show>
          </div>
        </div>
      </section>

      <section class="bg-white py-16">
        <div class="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[1.28fr_0.72fr] lg:items-center lg:px-8">
          <div class="order-2 p-0 sm:p-2 lg:order-1 lg:pr-2">
            <Show
              when={!adjustmentImageFailed()}
              fallback={
                <div class="flex min-h-[340px] w-full items-center justify-center rounded-2xl bg-slate-50/70 p-6 text-center text-sm text-slate-500">
                  {t('feature.inventory.visual2Caption')}
                </div>
              }
            >
              <img
                src={localizedAdjustmentImage()}
                alt={t('feature.inventory.visual2Title')}
                class="block h-auto w-full rounded-2xl object-contain shadow-[0_18px_48px_rgba(15,23,42,0.12)]"
                loading="lazy"
                onError={() => setAdjustmentImageFailed(true)}
              />
            </Show>
          </div>
          <div class="order-1 p-2 sm:p-4 lg:order-2 lg:pl-2">
            <h2 class="text-3xl font-black text-slate-900">
              {t('feature.inventory.f2')}
            </h2>
            <p class="mt-4 text-base leading-relaxed text-slate-700">
              {t('feature.inventory.f2Desc')}
            </p>
            <ul class="mt-6 list-inside list-disc space-y-3 text-sm text-slate-700">
              <li>{t('feature.inventory.f2Bullet1')}</li>
              <li>{t('feature.inventory.f2Bullet2')}</li>
              <li>{t('feature.inventory.f2Bullet3')}</li>
            </ul>
          </div>
        </div>
      </section>

      <section class="bg-amber-50/55 py-16">
        <div class="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[0.72fr_1.28fr] lg:items-center lg:px-8">
          <div class="p-2 sm:p-4 lg:pr-2">
            <div class="flex items-center gap-2">
              <h2 class="text-3xl font-black text-slate-900">
                {t('feature.inventory.f3')}
              </h2>
              <span
                class="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800"
                title={t('feature.inventory.proTooltip')}
                aria-label={t('feature.inventory.proTooltip')}
              >
                Pro
              </span>
            </div>
            <p class="mt-4 text-base leading-relaxed text-slate-700">
              {t('feature.inventory.f3Desc')}
            </p>
            <ul class="mt-6 list-inside list-disc space-y-3 text-sm text-slate-700">
              <li>{t('feature.inventory.f3Bullet1')}</li>
              <li>{t('feature.inventory.f3Bullet2')}</li>
              <li>{t('feature.inventory.f3Bullet3')}</li>
            </ul>
          </div>
          <div class="p-0 sm:p-2 lg:pl-2">
            <Show
              when={!transferImageFailed()}
              fallback={
                <div class="flex min-h-[340px] w-full items-center justify-center rounded-2xl bg-slate-50/70 p-6 text-center text-sm text-slate-500">
                  {t('feature.inventory.visual3Caption')}
                </div>
              }
            >
              <img
                src={localizedTransferImage()}
                alt={t('feature.inventory.visual3Title')}
                class="block h-auto w-full rounded-2xl object-contain shadow-[0_18px_48px_rgba(15,23,42,0.12)]"
                loading="lazy"
                onError={() => setTransferImageFailed(true)}
              />
            </Show>
          </div>
        </div>
      </section>

      <section class="bg-white py-16">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div class="mx-auto max-w-2xl text-center">
            <h2 class="text-3xl font-black text-slate-900">
              {t('feature.inventory.f4')}
            </h2>
            <p class="mt-4 text-base leading-relaxed text-slate-600">
              {t('feature.inventory.f4Desc')}
            </p>
          </div>

          <div class="mt-10 flex flex-col items-center gap-4 lg:flex-row lg:items-center lg:justify-center lg:gap-0">
            {/* Spreadsheet */}
            <div class="w-full lg:w-[44%]">
              <Show
                when={!csvSpreadsheetFailed()}
                fallback={
                  <div class="flex min-h-[260px] w-full items-center justify-center rounded-2xl bg-slate-50 p-6 text-center text-sm text-slate-400">
                    {t('feature.inventory.f4SpreadsheetAlt')}
                  </div>
                }
              >
                <img
                  src={localizedCsvSpreadsheet()}
                  alt={t('feature.inventory.f4SpreadsheetAlt')}
                  class="block h-auto w-full rounded-2xl object-contain shadow-[0_18px_48px_rgba(15,23,42,0.12)]"
                  loading="lazy"
                  onError={() => setCsvSpreadsheetFailed(true)}
                />
              </Show>
            </div>

            {/* khoai.io transformer arrow */}
            <div class="flex shrink-0 flex-col items-center gap-1 px-4 lg:px-6">
              <svg
                class="h-8 w-8 rotate-90 text-amber-500 lg:rotate-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
              <span class="rounded-full bg-amber-100 px-3 py-1 text-xs font-bold tracking-wide text-amber-800">
                khoai.io
              </span>
            </div>

            {/* App result table */}
            <div class="w-full lg:w-[44%]">
              <Show
                when={!csvResultFailed()}
                fallback={
                  <div class="flex min-h-[260px] w-full items-center justify-center rounded-2xl bg-slate-50 p-6 text-center text-sm text-slate-400">
                    {t('feature.inventory.f4TableAlt')}
                  </div>
                }
              >
                <img
                  src={localizedCsvResult()}
                  alt={t('feature.inventory.f4TableAlt')}
                  class="block h-auto w-full rounded-2xl object-contain shadow-[0_18px_48px_rgba(15,23,42,0.12)]"
                  loading="lazy"
                  onError={() => setCsvResultFailed(true)}
                />
              </Show>
            </div>
          </div>
        </div>
      </section>

      <section class="bg-white py-16">
        <div class="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <h2 class="text-4xl font-black text-slate-900">
            {t('feature.inventory.cta')}
          </h2>
          <p class="mx-auto mt-4 max-w-2xl text-base text-slate-700">
            {t('feature.inventory.ctaSubtitle')}
          </p>
          <A
            href="/signup"
            onClick={() =>
              trackMarketingEvent('feature_cta_click', {
                feature: 'inventory-management',
                target: '/signup',
              })
            }
            class="mt-8 inline-block rounded-xl bg-amber-600 px-8 py-3.5 text-lg font-bold text-white hover:bg-amber-700"
          >
            {t('feature.cta')}
          </A>
        </div>
      </section>

      <FeatureFaqSection
        titleKey="feature.inventory.faq.title"
        scriptId="ld-json-inventory-faq"
        items={[
          {
            q: t('feature.inventory.faq.q1'),
            a: t('feature.inventory.faq.a1'),
          },
          {
            q: t('feature.inventory.faq.q2'),
            a: t('feature.inventory.faq.a2'),
          },
          {
            q: t('feature.inventory.faq.q3'),
            a: t('feature.inventory.faq.a3'),
          },
          {
            q: t('feature.inventory.faq.q4'),
            a: t('feature.inventory.faq.a4'),
          },
          {
            q: t('feature.inventory.faq.q5'),
            a: t('feature.inventory.faq.a5'),
          },
        ]}
      />
    </FeatureMarketingLayout>
  );
}
